import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Download, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { weightTableData } from '../data/weightTables';
import { useLanguage } from '../i18n/LanguageContext';

const WeightTable = () => {
  const { tableId } = useParams();
  const { language } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  
  const tableData = weightTableData[tableId];

  if (!tableData) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {language === 'id' ? 'Tabel Tidak Ditemukan' : 'Table Not Found'}
          </h1>
          <Link to="/products">
            <Button className="bg-[#0693E3] hover:bg-[#0578BE]">
              <ArrowLeft className="mr-2" size={20} />
              {language === 'id' ? 'Kembali ke Produk' : 'Back to Products'}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const tableName = language === 'id' ? tableData.name : tableData.nameEn;
  const tableDesc = language === 'id' ? tableData.description : tableData.descriptionEn;
  const headers = language === 'id' ? tableData.headers : tableData.headersEn;

  // Filter data based on search
  const filteredData = tableData.data.filter(row =>
    row.some(cell => cell.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      {/* Header */}
      <section className="py-12 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="container mx-auto px-4">
          <Link to="/products" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="mr-2" size={20} />
            {language === 'id' ? 'Kembali ke Produk' : 'Back to Products'}
          </Link>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-5xl font-bold mb-4">{tableName}</h1>
              <p className="text-xl text-white/90">{tableDesc}</p>
            </div>
            <Button 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white hover:text-gray-900"
              onClick={() => window.print()}
            >
              <Download className="mr-2" size={20} />
              {language === 'id' ? 'Cetak / Download' : 'Print / Download'}
            </Button>
          </div>
        </div>
      </section>

      {/* Search Bar */}
      <section className="py-6 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="max-w-md relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <Input
              type="text"
              placeholder={language === 'id' ? 'Cari ukuran...' : 'Search size...'}
              className="pl-12 h-12"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </section>

      {/* Table */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#0693E3] text-white">
                  <tr>
                    <th className="px-6 py-4 text-left font-semibold">No</th>
                    {headers.map((header, index) => (
                      <th key={index} className="px-6 py-4 text-left font-semibold">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((row, rowIndex) => (
                    <tr 
                      key={rowIndex}
                      className={`border-b hover:bg-blue-50 transition-colors ${
                        rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                      }`}
                    >
                      <td className="px-6 py-4 font-medium text-gray-900">{rowIndex + 1}</td>
                      {row.map((cell, cellIndex) => (
                        <td key={cellIndex} className="px-6 py-4 text-gray-700">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {filteredData.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-600 text-lg">
                  {language === 'id' ? 'Tidak ada data yang sesuai dengan pencarian.' : 'No data matches your search.'}
                </p>
              </div>
            )}
          </Card>

          {/* Info Note */}
          <div className="mt-8 bg-blue-50 border-l-4 border-[#0693E3] p-6 rounded-lg">
            <h3 className="font-bold text-gray-900 mb-2">
              {language === 'id' ? 'Catatan Penting:' : 'Important Note:'}
            </h3>
            <ul className="text-gray-700 space-y-2">
              <li>• {language === 'id' ? 'Berat yang tercantum adalah berat teoritis per meter panjang' : 'Weights shown are theoretical weight per meter length'}</li>
              <li>• {language === 'id' ? 'Berat aktual dapat bervariasi ±5% tergantung toleransi produksi' : 'Actual weight may vary ±5% depending on production tolerance'}</li>
              <li>• {language === 'id' ? 'Untuk spesifikasi dan kebutuhan khusus, silakan hubungi tim kami' : 'For special specifications and requirements, please contact our team'}</li>
            </ul>
          </div>

          {/* CTA */}
          <div className="mt-8 text-center">
            <Link to="/contact">
              <Button size="lg" className="bg-[#CF2E2E] hover:bg-[#B02525] text-white">
                {language === 'id' ? 'Minta Penawaran' : 'Request Quotation'}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WeightTable;